/**
 * ConfigLookupDemoPanel — C085 중앙 설정 관리 데모 (queue #646 §R646-2 항목5)
 * never-throw 조회 — key+default 입력 → value+source(FOUND/DEFAULT)+type 표시.
 * 보조로 활성 설정 목록(마스킹) 자동 조회. read-only.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfigEntries, useConfigLookup } from '../../hooks/useConfigDemo';
import {
  cardStyle, sectionTitleStyle, subtitleStyle, labelStyle, inputStyle, monoStyle,
  primaryButtonStyle, resultBoxStyle, DemoErrorState, DemoEmptyHint,
} from './demoShared';

export function ConfigLookupDemoPanel() {
  const { t } = useTranslation();
  const lookupMutation = useConfigLookup();
  const entriesQuery = useConfigEntries();

  const [key, setKey] = useState('showcase.demo.sample-key');
  const [defaultValue, setDefaultValue] = useState('default-value');

  return (
    <section aria-labelledby="config-panel-title">
      <h2 id="config-panel-title" style={sectionTitleStyle}>{t('showcase.demo.config.title')}</h2>
      <p style={subtitleStyle}>{t('showcase.demo.config.subtitle')}</p>

      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
          <div>
            <label htmlFor="config-key" style={labelStyle}>{t('showcase.demo.config.keyLabel')}</label>
            <input id="config-key" type="text" value={key} onChange={(e) => setKey(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label htmlFor="config-default" style={labelStyle}>{t('showcase.demo.config.defaultLabel')}</label>
            <input id="config-default" type="text" value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)} style={inputStyle} />
          </div>
        </div>

        <button
          type="button"
          onClick={() => lookupMutation.mutate({ key, defaultValue })}
          disabled={lookupMutation.isPending || !key.trim()}
          aria-busy={lookupMutation.isPending}
          style={{ ...primaryButtonStyle, opacity: lookupMutation.isPending ? 0.6 : 1 }}
        >
          {lookupMutation.isPending ? t('showcase.demo.config.looking') : t('showcase.demo.config.lookupButton')}
        </button>

        <div style={{ marginTop: 'var(--spacing-4)' }}>
          {lookupMutation.isError && (
            <DemoErrorState title={t('showcase.demo.common.callFailed')} error={lookupMutation.error} />
          )}
          {!lookupMutation.isError && !lookupMutation.data && <DemoEmptyHint text={t('showcase.demo.config.empty')} />}
          {lookupMutation.data && !lookupMutation.isError && (
            <div role="region" aria-label={t('showcase.demo.config.resultLabel')} style={resultBoxStyle}>
              <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-2) var(--spacing-4)', margin: 0, fontSize: 'var(--font-size-sm)' }}>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.config.valueLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle }}>{lookupMutation.data.value}</dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.config.sourceLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle, fontWeight: 700 }}>{lookupMutation.data.source}</dd>
                <dt style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>{t('showcase.demo.config.typeLabel')}</dt>
                <dd style={{ margin: 0, ...monoStyle }}>{lookupMutation.data.type}</dd>
              </dl>
            </div>
          )}
        </div>
      </div>

      {/* ★BE 는 {entries:[...]} 래핑 없이 ConfigEntryResponse[] 배열을 그대로 반환한다 */}
      {entriesQuery.data && entriesQuery.data.length > 0 && (
        <div style={{ marginTop: 'var(--spacing-4)' }}>
          <p style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-2)' }}>
            {t('showcase.demo.config.entriesLabel')}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)' }}>
            <thead>
              <tr>
                {['configKey', 'configValue', 'valueType'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: 'var(--spacing-1) var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)', color: 'var(--color-text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entriesQuery.data.map((entry) => (
                <tr key={entry.id}>
                  <td style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>{entry.configKey}</td>
                  <td style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>{entry.configValue}</td>
                  <td style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>{entry.valueType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
